import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import useAnchoredPopover from './useAnchoredPopover';
import './fields.scss';

/**
 * DateField — a reusable date (or date + time) picker.
 *
 * Drop-in replacement for `<Input type="date" />` / `<Input type="datetime-local" />`:
 * the whole field is a button (no more hunting for the tiny calendar icon), and
 * `onChange` / `onBlur` receive an event-shaped object
 * `{ target: { name, id, value, type } }` so Formik's `handleChange` /
 * `handleBlur` and any `e.target.value` handler keep working unchanged.
 *
 * `value` may be a `YYYY-MM-DD` string, a `YYYY-MM-DDTHH:mm` string, an ISO
 * string, a Date or a moment. The emitted value is always a string in
 * `valueFormat` (defaults to `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` with `withTime`),
 * and `''` when cleared.
 *
 * Usage:
 *   <DateField
 *     name="dob"
 *     value={validation.values.dob}
 *     onChange={validation.handleChange}
 *     onBlur={validation.handleBlur}
 *     min={minDob}
 *     max={maxDob}
 *     invalid={validation.touched.dob && !!validation.errors.dob}
 *   />
 */

const DATE_FORMAT = 'YYYY-MM-DD';
const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm';
const YEARS_PER_PAGE = 12;

const parseMoment = (value, format) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date || moment.isMoment(value)) {
    const m = moment(value);
    return m.isValid() ? m : null;
  }
  const str = String(value);
  let m = moment(str, [format, DATETIME_FORMAT, DATE_FORMAT], true);
  if (!m.isValid()) m = moment(str, moment.ISO_8601, true);
  if (!m.isValid()) m = moment(str);
  return m.isValid() ? m : null;
};

const clampToRange = (m, min, max) => {
  if (min && m.isBefore(min)) return min.clone();
  if (max && m.isAfter(max)) return max.clone();
  return m;
};

const pad = (n) => String(n).padStart(2, '0');

/* ------------------------------------------------------------- calendar */

const Calendar = ({ value, onSelect, min, max, initialMonth }) => {
  const [view, setView] = useState('days');
  const [cursor, setCursor] = useState(() =>
    (value || initialMonth || moment()).clone().startOf('month'),
  );

  useEffect(() => {
    if (value) setCursor(value.clone().startOf('month'));
  }, [value && value.format(DATE_FORMAT)]);

  const today = moment();
  const weekdays = useMemo(() => moment.weekdaysMin(), []);
  const months = useMemo(() => moment.monthsShort(), []);

  const dayDisabled = (day) =>
    (min && day.isBefore(min, 'day')) || (max && day.isAfter(max, 'day'));
  const monthDisabled = (m) =>
    (min && m.clone().endOf('month').isBefore(min, 'day')) ||
    (max && m.clone().startOf('month').isAfter(max, 'day'));
  const yearDisabled = (y) =>
    (min && y < min.year()) || (max && y > max.year());

  const yearPageStart = Math.floor(cursor.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE;

  const goPrev = () => {
    if (view === 'days') setCursor(cursor.clone().subtract(1, 'month'));
    else if (view === 'months') setCursor(cursor.clone().subtract(1, 'year'));
    else setCursor(cursor.clone().subtract(YEARS_PER_PAGE, 'year'));
  };
  const goNext = () => {
    if (view === 'days') setCursor(cursor.clone().add(1, 'month'));
    else if (view === 'months') setCursor(cursor.clone().add(1, 'year'));
    else setCursor(cursor.clone().add(YEARS_PER_PAGE, 'year'));
  };

  const prevDisabled =
    view === 'days'
      ? min && cursor.clone().subtract(1, 'month').isBefore(min, 'month')
      : view === 'months'
      ? min && cursor.year() - 1 < min.year()
      : min && yearPageStart - 1 < min.year();
  const nextDisabled =
    view === 'days'
      ? max && cursor.clone().add(1, 'month').isAfter(max, 'month')
      : view === 'months'
      ? max && cursor.year() + 1 > max.year()
      : max && yearPageStart + YEARS_PER_PAGE > max.year();

  let title;
  if (view === 'days') title = cursor.format('MMMM YYYY');
  else if (view === 'months') title = cursor.format('YYYY');
  else title = `${yearPageStart} – ${yearPageStart + YEARS_PER_PAGE - 1}`;

  const renderDays = () => {
    const start = cursor.clone().startOf('month').startOf('week');
    const end = cursor.clone().endOf('month').endOf('week');
    const days = [];
    for (let d = start.clone(); !d.isAfter(end, 'day'); d.add(1, 'day')) {
      days.push(d.clone());
    }
    return (
      <Fragment>
        <div className="sf-cal__grid">
          {weekdays.map((w) => (
            <div key={w} className="sf-cal__wd">
              {w}
            </div>
          ))}
        </div>
        <div className="sf-cal__grid">
          {days.map((day) => {
            const selected = value && day.isSame(value, 'day');
            return (
              <button
                key={day.format(DATE_FORMAT)}
                type="button"
                disabled={dayDisabled(day)}
                onClick={() => onSelect(day)}
                className={classNames('sf-cal__cell', {
                  'sf-cal__cell--out': !day.isSame(cursor, 'month'),
                  'sf-cal__cell--today': !selected && day.isSame(today, 'day'),
                  'sf-cal__cell--selected': selected,
                })}
              >
                {day.date()}
              </button>
            );
          })}
        </div>
      </Fragment>
    );
  };

  const renderMonths = () => (
    <div className="sf-cal__grid sf-cal__grid--months">
      {months.map((label, idx) => {
        const m = cursor.clone().month(idx);
        return (
          <button
            key={label}
            type="button"
            disabled={monthDisabled(m)}
            onClick={() => {
              setCursor(m.startOf('month'));
              setView('days');
            }}
            className={classNames('sf-cal__cell', {
              'sf-cal__cell--selected': value && m.isSame(value, 'month'),
              'sf-cal__cell--today':
                !(value && m.isSame(value, 'month')) && m.isSame(today, 'month'),
            })}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const renderYears = () => (
    <div className="sf-cal__grid sf-cal__grid--years">
      {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map(
        (year) => (
          <button
            key={year}
            type="button"
            disabled={yearDisabled(year)}
            onClick={() => {
              setCursor(cursor.clone().year(year));
              setView('months');
            }}
            className={classNames('sf-cal__cell', {
              'sf-cal__cell--selected': value && value.year() === year,
              'sf-cal__cell--today':
                !(value && value.year() === year) && today.year() === year,
            })}
          >
            {year}
          </button>
        ),
      )}
    </div>
  );

  return (
    <div>
      <div className="sf-cal__head">
        <button
          type="button"
          aria-label="Previous"
          className="sf-cal__nav"
          disabled={!!prevDisabled}
          onClick={goPrev}
        >
          <i className="mdi mdi-chevron-left" />
        </button>
        <button
          type="button"
          className="sf-cal__title"
          title={view === 'days' ? 'Pick month' : view === 'months' ? 'Pick year' : ''}
          onClick={() => setView(view === 'days' ? 'months' : 'years')}
          disabled={view === 'years'}
        >
          {title}
        </button>
        <button
          type="button"
          aria-label="Next"
          className="sf-cal__nav"
          disabled={!!nextDisabled}
          onClick={goNext}
        >
          <i className="mdi mdi-chevron-right" />
        </button>
      </div>
      {view === 'days' && renderDays()}
      {view === 'months' && renderMonths()}
      {view === 'years' && renderYears()}
    </div>
  );
};

Calendar.propTypes = {
  value: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  min: PropTypes.object,
  max: PropTypes.object,
  initialMonth: PropTypes.object,
};

/* ------------------------------------------------------------ component */

const DateField = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  min,
  max,
  placeholder,
  disabled = false,
  invalid = false,
  withTime = false,
  clearable = true,
  displayFormat,
  valueFormat,
  className,
  style,
}) => {
  const format = valueFormat || (withTime ? DATETIME_FORMAT : DATE_FORMAT);
  const inputType = withTime ? 'datetime-local' : 'date';
  const fieldId = id || name;

  const selected = useMemo(() => parseMoment(value, format), [value, format]);
  const minM = useMemo(() => parseMoment(min, format), [min, format]);
  const maxM = useMemo(() => parseMoment(max, format), [max, format]);

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const wasOpen = useRef(false);

  const close = useCallback(() => setOpen(false), []);
  const popStyle = useAnchoredPopover({ open, anchorRef, popRef, onClose: close });

  const emit = (m) => {
    if (!onChange) return;
    onChange({
      target: { name, id: fieldId, value: m ? m.format(format) : '', type: inputType },
    });
  };

  // Mirror native behaviour: leaving the field marks it as touched.
  useEffect(() => {
    if (wasOpen.current && !open && onBlur) {
      onBlur({
        target: {
          name,
          id: fieldId,
          value: selected ? selected.format(format) : '',
          type: inputType,
        },
      });
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (open) setPending(selected);
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    setOpen((v) => !v);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  const initialMonth = useMemo(
    () => clampToRange(moment(), minM, maxM).startOf('month'),
    [minM, maxM],
  );

  const handleDay = (day) => {
    if (withTime) {
      const base = pending || clampToRange(moment(), minM, maxM);
      const next = day.clone().hour(base.hour()).minute(base.minute()).second(0);
      setPending(clampToRange(next, minM, maxM));
      return;
    }
    emit(day.clone().startOf('day'));
    close();
  };

  const setTime = (hours, minutes) => {
    const h = Number.isFinite(hours) ? Math.min(23, Math.max(0, hours)) : 0;
    const m = Number.isFinite(minutes) ? Math.min(59, Math.max(0, minutes)) : 0;
    const base = pending || clampToRange(moment(), minM, maxM);
    setPending(clampToRange(base.clone().hour(h).minute(m).second(0), minM, maxM));
  };

  const setNow = () => setPending(clampToRange(moment().second(0), minM, maxM));

  const apply = () => {
    if (pending) emit(pending);
    close();
  };

  const clear = () => {
    emit(null);
    setPending(null);
    close();
  };

  const today = moment();
  const todayAllowed =
    !(minM && today.isBefore(minM, 'day')) && !(maxM && today.isAfter(maxM, 'day'));

  const display = selected
    ? selected.format(displayFormat || (withTime ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY'))
    : '';

  const calendarValue = withTime ? pending : selected;

  return (
    <div
      className={classNames('sf-wrap', { 'is-invalid': invalid }, className)}
      onKeyDown={onKeyDown}
    >
      <button
        ref={anchorRef}
        type="button"
        id={fieldId}
        name={name}
        disabled={disabled}
        style={style}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className={classNames('form-control sf-field', { 'is-invalid': invalid })}
      >
        <span className="sf-field__text">
          {display || (
            <span className="sf-field__placeholder">
              {placeholder || (withTime ? 'Select date & time' : 'Select date')}
            </span>
          )}
        </span>
        <i className="mdi mdi-calendar-blank-outline sf-field__icon" />
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label={withTime ? 'Choose date and time' : 'Choose date'}
            className="sf-pop sf-cal"
            style={popStyle}
            onKeyDown={onKeyDown}
          >
            <Calendar
              value={calendarValue}
              onSelect={handleDay}
              min={minM}
              max={maxM}
              initialMonth={initialMonth}
            />

            {withTime && (
              <div className="sf-cal__time">
                <span className="sf-cal__time-label">Time</span>
                <input
                  type="number"
                  min="0"
                  max="23"
                  aria-label="Hours"
                  className="form-control form-control-sm"
                  value={pending ? pad(pending.hour()) : ''}
                  placeholder="HH"
                  onChange={(e) =>
                    setTime(Number(e.target.value), pending ? pending.minute() : 0)
                  }
                />
                <span>:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  aria-label="Minutes"
                  className="form-control form-control-sm"
                  value={pending ? pad(pending.minute()) : ''}
                  placeholder="MM"
                  onChange={(e) =>
                    setTime(pending ? pending.hour() : 0, Number(e.target.value))
                  }
                />
                <button
                  type="button"
                  className="btn btn-sm btn-soft-secondary"
                  onClick={setNow}
                >
                  Now
                </button>
              </div>
            )}

            <div className="sf-cal__foot">
              {clearable && selected && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger sf-cal__foot-left"
                  onClick={clear}
                >
                  Clear
                </button>
              )}
              {withTime ? (
                <Fragment>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={close}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={!pending}
                    onClick={apply}
                  >
                    Apply
                  </button>
                </Fragment>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  disabled={!todayAllowed}
                  onClick={() => handleDay(today)}
                >
                  Today
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

DateField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  withTime: PropTypes.bool,
  clearable: PropTypes.bool,
  displayFormat: PropTypes.string,
  valueFormat: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default DateField;
